function titleCase(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function extractEmailAddress(value: string) {
  const match = value.match(/<([^<>@\s]+@[^<>@\s]+\.[^<>@\s]+)>/);
  return (match?.[1] || value).trim().toLowerCase();
}

/**
 * The canonical contact resolver exposes the current addresses as
 * `companyEmail`/`privateEmail` (and historical addresses through
 * `contact_emails`). Consumers must never depend on a non-canonical `email`
 * convenience property, otherwise an existing contact is shown as a new one.
 */
export function crmRecordMatchesEmail(record: any, email: string) {
  const normalized = extractEmailAddress(email);
  return [record?.companyEmail, record?.privateEmail, record?.email]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .some((value) => extractEmailAddress(value) === normalized);
}

function emailLocalNameParts(email: string) {
  const normalizedEmail = extractEmailAddress(email);
  const [localPart = ""] = normalizedEmail.split("@");
  return localPart
    .replace(/[._+-]+/g, " ")
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function companyNameFromEmail(email: string) {
  const domain = extractEmailAddress(email).split("@")[1] || "";
  const root = domain.split(".").filter(Boolean)[0] || "Azienda da valutare";
  return titleCase(root.replace(/[-_]+/g, " "));
}

export function looksLikeGenericEmailAddress(email: string) {
  const normalizedEmail = extractEmailAddress(email);
  const [localPart = ""] = normalizedEmail.split("@");
  return [
    "info",
    "hello",
    "sales",
    "commerciale",
    "marketing",
    "newsletter",
    "news",
    "noreply",
    "no-reply",
    "notification",
    "notifiche",
    "support",
    "admin",
  ].some((token) => localPart.includes(token));
}

export function buildContactCandidateFromEmail(email: string, source: "recipient" | "sender" = "recipient") {
  const normalizedEmail = extractEmailAddress(email);
  const nameParts = emailLocalNameParts(normalizedEmail);
  const firstName = titleCase(nameParts[0] || "Nuovo");
  const lastName = titleCase(nameParts.slice(1).join(" "));
  const origin = source === "sender" ? "mittente email" : "destinatario";

  return {
    firstName,
    lastName,
    companyEmail: normalizedEmail,
    tags: ["email-candidate"],
    notes: `Candidato contatto creato manualmente dal modulo Email per il ${origin} ${normalizedEmail}. Verificare nome, cognome, azienda e ruolo prima di salvare.`,
  };
}

export function buildContactCandidateFromEmailIdentity(input: {
  email: string;
  displayName?: string | null;
  source?: "recipient" | "sender";
}) {
  const normalizedEmail = extractEmailAddress(input.email);
  const normalizedDisplayName = (input.displayName || "").trim().replace(/^['"]|['"]$/g, "");
  const displayLooksLikeAddress = normalizedDisplayName.includes("@")
    || extractEmailAddress(normalizedDisplayName) === normalizedEmail;
  const displayParts = displayLooksLikeAddress
    ? []
    : normalizedDisplayName.split(/\s+/).map((part) => part.trim()).filter(Boolean);
  const fallback = buildContactCandidateFromEmail(normalizedEmail, input.source);

  if (displayParts.length === 0) return fallback;
  return {
    ...fallback,
    firstName: titleCase(displayParts[0]),
    lastName: titleCase(displayParts.slice(1).join(" ")),
  };
}

export function openMissingEmailContactCandidate(email: string) {
  document.dispatchEvent(new CustomEvent("openContactModal", {
    detail: {
      initialData: buildContactCandidateFromEmail(email),
      source: "email-recipient-picker",
    },
  }));
}

export function buildLeadCandidateFromEmail(email: string, sourceLabel = "mittente email") {
  const normalizedEmail = extractEmailAddress(email);
  return {
    company: companyNameFromEmail(normalizedEmail),
    companyEmail: normalizedEmail,
    source: ["email"],
    status: "New",
    notes: `Lead candidato da valutare creato dal modulo Email per ${sourceLabel} ${normalizedEmail}. Non salvare finche' non sono stati verificati azienda, referente e rilevanza.`,
  };
}

export function openEmailSenderContactCandidate(email: string) {
  document.dispatchEvent(new CustomEvent("openContactModal", {
    detail: {
      initialData: buildContactCandidateFromEmail(email, "sender"),
      source: "email-sender-candidate",
    },
  }));
}

export function openEmailSenderLeadCandidate(email: string) {
  document.dispatchEvent(new CustomEvent("openLeadModal", {
    detail: {
      initialData: buildLeadCandidateFromEmail(email),
      source: "email-sender-candidate",
    },
  }));
}
